import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { Toaster, toast } from 'sonner';
import './lib/i18n';
import { routeTree } from './routeTree.gen';
import { useAuthStore } from './stores/authStore';
import { SessionExpiredError, ApiError } from './api/errors';
import { RESUME_OTP_MESSAGE, resumeOtpTarget } from './api/services/retail-wizard/resumeOtpGuard';
import { RETAILER_INTEGRATION_KEY } from './domains/retail/admin/integration/api/useRetailerIntegration';
import { isManagedByIntegrationError } from './domains/retail/admin/integration/lib/integrationLock';

const router = createRouter({
    routeTree,
    context: {
        auth: { ensureLoaded: () => useAuthStore.getState().ensureLoaded() },
    },
    defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
}

/**
 * Errores que no se resuelven en la pantalla donde ocurren: cambian de pantalla.
 *
 * DEC-008: sesión muerta → login. Y una venta retomada que todavía no probó el teléfono → el paso del
 * código, porque el silo rebota TODAS sus mutaciones hasta entonces y sin esto el vendedor solo veía
 * un error suelto en una pantalla que ya no puede guardar nada.
 */
function handleGlobalError(error: unknown): void {
    if (error instanceof SessionExpiredError) {
        useAuthStore.getState().clear();
        void router.navigate({ to: '/login' });
        return;
    }
    // El back ya trata la cadena como integrada: se relee para que los candados aparezcan.
    if (isManagedByIntegrationError(error)) {
        void queryClient.invalidateQueries({ queryKey: RETAILER_INTEGRATION_KEY });
        return;
    }
    const resume = resumeOtpTarget(error);
    if (!resume) return;
    // `id` fijo: una pantalla que rebota varias llamadas deja UN aviso, no una pila.
    toast.error(RESUME_OTP_MESSAGE, { id: 'resume-otp' });
    void router.navigate({
        to: '/wizard/s/$sessionId/$step',
        params: { sessionId: resume.sessionId, step: 'otp' },
        // `replace` para que "atrás" no devuelva a la pantalla que acaba de rebotar.
        replace: true,
    });
}

const queryClient = new QueryClient({
    queryCache: new QueryCache({ onError: handleGlobalError }),
    defaultOptions: {
        queries: {
            retry: (failureCount, error) => {
                if (error instanceof ApiError && error.httpStatus >= 400 && error.httpStatus < 500)
                    return false;
                return failureCount < 2;
            },
        },
        mutations: { onError: handleGlobalError },
    },
});

export function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
            <Toaster richColors containerAriaLabel="Notificaciones" />
        </QueryClientProvider>
    );
}
